import { useCallback } from 'react'
import { isLocalhost } from '@dhl/utils'
import { addRequestInterceptor } from '@dhl/fetch'
import { useRequestHeader } from '@dhl/axios'

// Used when XSRF-TOKEN cookie name is dependent on REACT_APP_ENVIRONMENT
export function getXsrfTokenFromCookie(environment?: string): string | undefined {
  const cookieName = ['XSRF-TOKEN', environment].filter(Boolean).join('-')
  const xsrfCookie = document.cookie.split(';').find((value) => value.trim().startsWith(cookieName))
  return xsrfCookie ? xsrfCookie.split('=')[1] : undefined
}

/**
 * Setup an Axios request interceptor for adding the correct XSRF token header for local development.
 * Use this method for adding the interceptor to the global Axios instance.
 */
export function setupLocalXsrfRequestInterceptor(environment?: string): void {
  if (isLocalhost() && !window?.Cypress) {
    addRequestInterceptor({
      onRequest: (request) => {
        const cookieValue = getXsrfTokenFromCookie(environment)
        return {
          ...request,
          headers: cookieValue ? request.headers.set('X-XSRF-TOKEN', cookieValue) : request.headers,
        }
      },
    })
  }
}

/**
 * Setup an Axios request interceptor for adding the correct XSRF token header for local development.
 * This hook is required for APIs using the new @dhl/orval mutator, since those mutators use a shared
 * Axios instance (instead of the global one) on which the interceptor needs to be configured.
 */
export function useSetupLocalXsrfRequestInterceptor(environment?: string): void {
  const getCookieValue = useCallback(
    () => (isLocalhost() && !window?.Cypress ? (getXsrfTokenFromCookie(environment) ?? '') : ''),
    [environment]
  )

  useRequestHeader('X-XSRF-TOKEN', getCookieValue)
}
